import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useKmClient } from '../../../lib/useKmClient.js';
import { useKmQuery } from '../hooks/useKmQuery.js';
import { useAuth as useRealAuth } from '../../../context/AuthContext';
import { canManageEsperienze } from '../../../lib/roles.js';
import ErrorDetails from '../components/ErrorDetails.jsx';
import { PREAVVISI, anteprimaPromemoria, formatDataIt, oggiIso, labelPreavviso } from '../preavvisi.js';
import { useFiltriEsperienze, nomeDaCodice } from '../hooks/useTassonomie.js';

/* Riscritto sul contratto reale (Collaudo_KM_TalentGPT_20260907_2.docx,
   §6.9): GET /esperienze/{id} usa campi con prefisso "se_0NN_..." (mappano
   una scheda esperienza a sezioni numerate), completamente diversi da
   quelli assunti prima (denominazione_progetto, risorsa_nome,
   cliente_finale, capacita_percentuale... nessuno di questi esiste qui):

   se_002_cliente, se_003_settore, se_004_dominio,
   se_005_contesto_contrattuale, se_006_ruolo, se_007_periodo (stringa
   già formattata, non date separate), se_008_tecnologie, se_009_presenza_isa,
   se_010_citabilita, se_011_stato, se_012_ultima_compilazione, se_013_sommario,
   se_016_utilizzabile, se_017_nota_sales, se_018/019/020_blocco_a/b/c.

   Corretto il 22/09 (Monaco): GET /esperienze/{id} ora include davvero
   data_chiusura_prevista, data_fine_dichiarata, fonte_data_fine,
   data_riferimento_chiusura (quella valida da usare nel form — la
   dichiarata se c'è, altrimenti la prevista) e preavvisi_giorni — prima
   mancavano dalla vista sottostante, non solo dalla query. Il form ora
   precompila con il valore attuale invece di restare sempre vuoto.

   PATCH .../data-chiusura-prevista accetta anche preavvisi_giorni (array
   di interi) oltre alla data.

   POST /esperienze/{id}/close: i campi reali sono data_fine_effettiva,
   feedback_performance E feedback_qualita_progetto (due campi distinti,
   non un "feedback_cliente" unico come assunto prima). */

/* 28/09 — allineamento all'AF v2.0:
   - intestazione con i campi della scheda esperienza (§6, tabella dei campi):
     progetto, cliente, settore e dominio per nome, capacità, tariffa,
     referente, attività, date; tolti contesto contrattuale e presenza ISA,
     che l'AF rinvia alla fase 2;
   - UC-P03-02: preavvisi come caselle (2 mesi … 1 giorno), promemoria alla
     data sempre attivo (V25), nota di motivazione, anteprima delle date
     effettive, avviso per i preavvisi già trascorsi (FA3), blocco per data
     anteriore all'apertura (FA1) e conferma esplicita per data già passata
     (FA2). La data mostrata è quella PREVISTA: è il solo dato che governa i
     promemoria; la data comunicata dalla risorsa è solo informativa;
   - UC-P03-03: aggiunte le note di chiusura;
   - marcatura commerciale a tre stati: non marcata / utilizzabile / non
     utilizzabile (prima «non utilizzabile» appariva come «Non marcata»). */

function Campo({ label, children }) {
  return (
    <div>
      <dt className="field-label">{label}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </div>
  );
}

// 16/10 — FE-06: citabilità con il nome (valori ammessi dalla migration 045).
const CITABILITA_ETICHETTE = {
  libera: 'Citabile liberamente',
  anonimizzata: 'Citabile con anonimizzazione',
  non_citabile: 'Non citabile',
};

export function etichettaUtilizzabile(valore) {
  if (valore === true) return { testo: 'Utilizzabile commercialmente', classe: 'badge-success' };
  if (valore === false) return { testo: 'Non utilizzabile commercialmente', classe: 'badge-warning' };
  return { testo: 'Non marcata', classe: 'badge-neutral' };
}

export default function EsperienzaDetailPage() {
  const { esperienzaId } = useParams();
  const kmClient = useKmClient('km');
  // Ruolo vero da /users/me, non il simulatore KM (fissato ad admin dal
  // 28/09): la UI deve mostrare solo ciò che il ruolo reale consente.
  const { user: realUser } = useRealAuth();
  const role = realUser?.role;
  const canManage = canManageEsperienze(role);
  const { filtri } = useFiltriEsperienze();

  const [newDate, setNewDate] = useState(null);
  const [newPreavvisi, setNewPreavvisi] = useState(null); // array di giorni, null = valore corrente
  const [nota, setNota] = useState('');
  const [confermaPassata, setConfermaPassata] = useState(false);
  const [savingDate, setSavingDate] = useState(false);
  const [dateMessage, setDateMessage] = useState(null);

  const [closeDate, setCloseDate] = useState(() => oggiIso());
  const [feedbackPerformance, setFeedbackPerformance] = useState('');
  const [feedbackQualita, setFeedbackQualita] = useState('');
  const [noteChiusura, setNoteChiusura] = useState('');
  const [closing, setClosing] = useState(false);
  // 16/10 — V13: capacità garantita modificabile da HR in ogni momento
  // (PATCH /esperienze/{id}/capacita, valore in % tra 0 escluso e 100).
  const [capacitaInModifica, setCapacitaInModifica] = useState(false);
  const [nuovaCapacita, setNuovaCapacita] = useState('');
  const [salvandoCapacita, setSalvandoCapacita] = useState(false);
  const [erroreCapacita, setErroreCapacita] = useState(null);
  const [actionError, setActionError] = useState(null);

  const { data: esp, status, error, refetch } = useKmQuery(
    () => kmClient.get(`/esperienze/${esperienzaId}`),
    [esperienzaId]
  );

  if (status === 'loading') {
    return (
      <div className="card">
        <div className="skeleton-line" style={{ width: '50%' }} />
        <div className="skeleton-line" style={{ width: '70%' }} />
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="state-error">
        {error?.status === 404 ? 'Esperienza non trovata.' : 'Errore nel caricamento.'}
        <div><button type="button" className="btn-link" onClick={refetch}>Riprova</button></div>
        <ErrorDetails error={error} />
      </div>
    );
  }

  const statoEsperienza = esp?.se_011_stato ?? esp?.stato ?? null;
  const isClosed = statoEsperienza === 'chiusa';
  const oggi = oggiIso();

  const dataCorrente = esp?.data_chiusura_prevista ? String(esp.data_chiusura_prevista).slice(0, 10) : '';
  const preavvisiCorrenti = Array.isArray(esp?.preavvisi_giorni) ? esp.preavvisi_giorni.map(Number) : [];
  const dataForm = newDate ?? dataCorrente;
  const preavvisiForm = newPreavvisi ?? preavvisiCorrenti;
  const dataApertura = esp?.data_apertura || esp?.data_inizio ? String(esp.data_apertura || esp.data_inizio).slice(0, 10) : '';
  const primaDellApertura = Boolean(dataForm && dataApertura && dataForm < dataApertura);
  const giaTrascorsa = Boolean(dataForm && dataForm < oggi);
  const modificato = dataForm !== dataCorrente
    || [...preavvisiForm].sort().join(',') !== [...preavvisiCorrenti].sort().join(',');
  const anteprima = anteprimaPromemoria(dataForm, preavvisiForm);
  const preavvisiTrascorsi = anteprima.filter((v) => !v.obbligatorio && v.trascorso);

  function togglePreavviso(giorni) {
    const base = newPreavvisi ?? preavvisiCorrenti;
    setNewPreavvisi(base.includes(giorni) ? base.filter((g) => g !== giorni) : [...base, giorni]);
  }

  async function handleSaveDate() {
    if (!dataForm || primaDellApertura || (giaTrascorsa && !confermaPassata)) return;
    setSavingDate(true);
    setActionError(null);
    setDateMessage(null);
    try {
      await kmClient.patch(`/esperienze/${esperienzaId}/data-chiusura-prevista`, {
        data_chiusura_prevista: dataForm,
        preavvisi_giorni: [...preavvisiForm].sort((a, b) => b - a),
        nota: nota.trim() || null,
      });
      // UC-P03-02 passo 12: conferma con l'elenco dei promemoria attivi.
      const attivi = anteprima.filter((v) => !v.trascorso);
      setDateMessage(`Data aggiornata. Promemoria attivi: ${attivi.length ? attivi.map((v) => `${v.label} (${formatDataIt(v.data)})`).join('; ') : 'nessuno futuro'}.`);
      setNewDate(null);
      setNewPreavvisi(null);
      setNota('');
      setConfermaPassata(false);
      refetch();
    } catch (err) {
      setActionError(err?.detail || 'Non è stato possibile aggiornare la data.');
    } finally {
      setSavingDate(false);
    }
  }

  async function handleSaveCapacita() {
    const valore = Number(String(nuovaCapacita).replace(',', '.'));
    if (!Number.isFinite(valore) || valore <= 0 || valore > 100) {
      setErroreCapacita('Inserire una percentuale maggiore di 0 e non superiore a 100.');
      return;
    }
    setSalvandoCapacita(true);
    setErroreCapacita(null);
    try {
      await kmClient.patch(`/esperienze/${esperienzaId}/capacita`, { capacita_garantita: valore });
      setCapacitaInModifica(false);
      refetch();
    } catch (err) {
      setErroreCapacita(err?.detail || 'Salvataggio non riuscito.');
    } finally {
      setSalvandoCapacita(false);
    }
  }

  async function handleClose() {
    if (!closeDate || closeDate > oggi || !feedbackPerformance.trim() || !feedbackQualita.trim()) return;
    setClosing(true);
    setActionError(null);
    try {
      await kmClient.post(`/esperienze/${esperienzaId}/close`, {
        data_fine_effettiva: closeDate,
        feedback_performance: feedbackPerformance.trim(),
        feedback_qualita_progetto: feedbackQualita.trim(),
        note_chiusura: noteChiusura.trim() || null,
      });
      refetch();
    } catch (err) {
      // V30: data futura rifiutata con 422; stesso codice se manca un feedback.
      setActionError(err?.detail || 'Chiusura non riuscita. Verificare data e feedback.');
    } finally {
      setClosing(false);
    }
  }

  const settoreL1 = nomeDaCodice(filtri?.settori, esp?.settore_l1);
  const settoreL2 = nomeDaCodice(filtri?.settori, esp?.settore_l2);
  const dominioL1 = nomeDaCodice(filtri?.domini, esp?.dominio_l1);
  const dominioL2 = nomeDaCodice(filtri?.domini, esp?.dominio_l2);
  const tecnologie = Array.isArray(esp?.tecnologie) ? esp.tecnologie : (Array.isArray(esp?.se_008_tecnologie) ? esp.se_008_tecnologie : []);
  const certificazioni = Array.isArray(esp?.certificazioni) ? esp.certificazioni : [];
  const utilizzabile = etichettaUtilizzabile(esp?.se_016_utilizzabile);

  return (
    <div>
      <Link to="/knowledge-management/esperienze" className="btn-link" style={{ marginBottom: 'var(--space-4)', display: 'inline-block' }}>
        ← Esperienze
      </Link>

      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
          <div>
            <h1 className="section-title" style={{ marginBottom: 'var(--space-1)' }}>
              {esp?.denominazione_progetto || esp?.se_002_cliente || 'Esperienza'}
            </h1>
            <p style={{ color: 'var(--color-ink-muted)', margin: 0 }}>
              {esp?.cliente_finale || esp?.se_002_cliente || 'Cliente non disponibile'}
              {' · '}{esp?.se_006_ruolo || 'Ruolo non disponibile'}
              {' · '}{esp?.se_007_periodo || '—'}
            </p>
          </div>
          <span className={`badge ${isClosed ? 'badge-neutral' : 'badge-success'}`}>
            {/* 16/10 — FE-06: stato senza codice E1/E2. */}
            {isClosed ? 'Chiusa' : 'In corso'}
          </span>
        </div>

        <dl style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginTop: 'var(--space-5)' }}>
          <Campo label="Risorsa">
            <span title={esp?.consulente_id || undefined}>{esp?.se_001_consulente || esp?.consulente_id || '—'}</span>
          </Campo>
          <Campo label="Seniority">{esp?.seniority ? esp.seniority.charAt(0).toUpperCase() + esp.seniority.slice(1) : '—'}</Campo>
          <Campo label="Settore L1 / L2">{settoreL1 || '—'}{settoreL2 ? ` · ${settoreL2}` : ''}</Campo>
          <Campo label="Dominio funzionale L1 / L2">{dominioL1 || '—'}{dominioL2 ? ` · ${dominioL2}` : ''}</Campo>
          <Campo label="Data di apertura">{formatDataIt(dataApertura)}</Campo>
          <Campo label="Data di chiusura prevista">
            {formatDataIt(dataCorrente)}
            {esp?.data_fine_dichiarata && (
              <span style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
                Fine comunicata dalla risorsa: {formatDataIt(esp.data_fine_dichiarata)} (solo informativa)
              </span>
            )}
          </Campo>
          {isClosed && <Campo label="Data di fine effettiva">{formatDataIt(esp?.data_chiusura)}</Campo>}
          <Campo label="Capacità garantita">
            {!capacitaInModifica ? (
              <>
                {esp?.capacita_garantita != null ? `${Number(esp.capacita_garantita)}%` : '—'}
                {canManage && (
                  <button
                    type="button"
                    className="btn-link"
                    style={{ marginLeft: 'var(--space-2)', fontSize: 12 }}
                    onClick={() => {
                      setNuovaCapacita(esp?.capacita_garantita != null ? String(Number(esp.capacita_garantita)) : '');
                      setErroreCapacita(null);
                      setCapacitaInModifica(true);
                    }}
                  >
                    Modifica
                  </button>
                )}
              </>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  inputMode="decimal"
                  className="field-input"
                  style={{ width: 80 }}
                  value={nuovaCapacita}
                  onChange={(e) => setNuovaCapacita(e.target.value)}
                  aria-label="Nuova capacità garantita in percentuale"
                  aria-invalid={erroreCapacita ? 'true' : undefined}
                />
                %
                <button type="button" className="btn btn-primary" disabled={salvandoCapacita} onClick={handleSaveCapacita}>
                  {salvandoCapacita ? 'Salvataggio…' : 'Salva'}
                </button>
                <button type="button" className="btn btn-secondary" disabled={salvandoCapacita} onClick={() => setCapacitaInModifica(false)}>
                  Annulla
                </button>
                {erroreCapacita && (
                  <span role="alert" style={{ display: 'block', width: '100%', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #b91c1c)' }}>{erroreCapacita}</span>
                )}
              </span>
            )}
          </Campo>
          <Campo label="Tariffa applicata">{esp?.tariffa_applicata != null ? esp.tariffa_applicata : '— (da staffing)'}</Campo>
          <Campo label="Referente commerciale">{esp?.referente_commerciale || '—'}</Campo>
          <Campo label="Citabilità">{CITABILITA_ETICHETTE[esp?.se_010_citabilita] || (esp?.se_010_citabilita ? String(esp.se_010_citabilita).replace(/_/g, ' ') : 'Non valorizzata')}</Campo>
        </dl>

        {esp?.attivita_svolte && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <dt className="field-label">Attività svolte</dt>
            <p style={{ margin: 'var(--space-1) 0 0', whiteSpace: 'pre-wrap' }}>{esp.attivita_svolte}</p>
          </div>
        )}

        {(tecnologie.length > 0 || certificazioni.length > 0) && (
          <div style={{ marginTop: 'var(--space-4)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <div>
              <dt className="field-label">Tecnologie</dt>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
                {tecnologie.length ? tecnologie.map((t) => <span key={t} className="badge badge-neutral">{t}</span>) : '—'}
              </div>
            </div>
            <div>
              <dt className="field-label">Certificazioni</dt>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
                {certificazioni.length ? certificazioni.map((t) => <span key={t} className="badge badge-neutral">{t}</span>) : '—'}
              </div>
            </div>
          </div>
        )}

        {esp?.note_libere && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <dt className="field-label">Note libere</dt>
            <p style={{ margin: 'var(--space-1) 0 0', whiteSpace: 'pre-wrap' }}>{esp.note_libere}</p>
          </div>
        )}

        {esp?.se_013_sommario && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <dt className="field-label">Sommario integrato</dt>
            <p style={{ margin: 'var(--space-1) 0 0' }}>{esp.se_013_sommario}</p>
            {esp?.se_012_ultima_compilazione && (
              <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
                Ultima compilazione ricevuta: {esp.se_012_ultima_compilazione}
              </p>
            )}
          </div>
        )}

        <div style={{ marginTop: 'var(--space-4)' }}>
          <span className={`badge ${utilizzabile.classe}`}>{utilizzabile.testo}</span>
          {esp?.se_017_nota_sales && (
            <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
              "{esp.se_017_nota_sales}"
            </span>
          )}
        </div>
      </div>

      {esp?.scheda_id && (
        <Link to={`/knowledge-management/schede/${esp.scheda_id}`} className="btn btn-secondary" style={{ marginBottom: 'var(--space-5)' }}>
          Apri scheda esperienza
        </Link>
      )}

      {!canManage && !isClosed && (
        <div className="card state-empty">
          Solo HUB HR può modificare la data di chiusura prevista o chiudere questa esperienza.
          Il tuo ruolo attuale è di sola consultazione.
        </div>
      )}

      {canManage && !isClosed && (
        <div className="card">
          <h2 className="section-title">Data di chiusura prevista e promemoria</h2>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-ink-muted)', marginTop: 0 }}>
            Attuale: <strong>{formatDataIt(dataCorrente)}</strong>
            {preavvisiCorrenti.length > 0 && ` — preavvisi: ${[...preavvisiCorrenti].sort((a, b) => b - a).map(labelPreavviso).join(', ')}`}
            {' '}+ promemoria alla data (sempre attivo).
          </p>

          {actionError && (
            <p className="state-error" style={{ padding: 'var(--space-2) 0', textAlign: 'left' }}>{actionError}</p>
          )}
          {dateMessage && (
            <p className="badge badge-success" style={{ display: 'block', whiteSpace: 'normal', padding: 'var(--space-2)' }}>{dateMessage}</p>
          )}

          <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 640 }}>
            <div>
              <label className="field-label" htmlFor="new-date">Data di chiusura prevista *</label>
              <input
                id="new-date"
                type="date"
                className="field-input"
                min={dataApertura || undefined}
                value={dataForm}
                onChange={(e) => { setNewDate(e.target.value); setConfermaPassata(false); setDateMessage(null); }}
              />
              {primaDellApertura && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #791F1F)', marginTop: 4 }}>
                  La data di chiusura prevista non può precedere la data di apertura ({formatDataIt(dataApertura)}).
                </div>
              )}
              {giaTrascorsa && !primaDellApertura && (
                <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 'var(--font-size-xs)', color: '#633806', marginTop: 4 }}>
                  <input type="checkbox" checked={confermaPassata} onChange={(e) => setConfermaPassata(e.target.checked)} />
                  La data è già trascorsa: l'esperienza rientrerà nel controllo giornaliero delle scadenze e riceverai
                  solo il promemoria alla data. Confermo (regolarizzazione di un ingaggio già concluso).
                </label>
              )}
            </div>

            <div>
              <span className="field-label" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>Preavvisi (facoltativi, multipli)</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                {PREAVVISI.map((p) => (
                  <label key={p.giorni} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-sm)' }}>
                    <input type="checkbox" checked={preavvisiForm.includes(p.giorni)} onChange={() => togglePreavviso(p.giorni)} />
                    {p.label}
                  </label>
                ))}
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-sm)', color: 'var(--color-ink-faint)' }}>
                  <input type="checkbox" checked disabled />
                  Alla data prevista (obbligatorio, non disattivabile)
                </label>
              </div>
            </div>

            {anteprima.length > 0 && (
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
                <div className="field-label">Anteprima promemoria</div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {anteprima.map((v) => (
                    <li key={v.giorni} style={{ color: v.trascorso ? '#633806' : undefined }}>
                      {v.label}: {formatDataIt(v.data)}{v.trascorso ? ' — già trascorsa, non sarà emesso' : ''}
                    </li>
                  ))}
                </ul>
                {preavvisiTrascorsi.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    I promemoria non sono retroattivi: la selezione resta valida per eventuali future modifiche della data.
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="field-label" htmlFor="nota-data">Nota di motivazione (facoltativa)</label>
              <input id="nota-data" className="field-input" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Es. proroga concordata con il cliente" />
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              style={{ justifySelf: 'start' }}
              disabled={!dataForm || !modificato || primaDellApertura || (giaTrascorsa && !confermaPassata) || savingDate}
              onClick={handleSaveDate}
            >
              {savingDate ? 'Salvataggio…' : 'Salva data e promemoria'}
            </button>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: 'var(--space-5) 0' }} />

          <h3 style={{ fontSize: 'var(--font-size-base)', margin: '0 0 var(--space-3)' }}>Chiudi esperienza</h3>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginTop: 0 }}>
            Il Self-Assessment di chiusura (SA-4) non parte ora: lo invia il ciclo giornaliero successivo alla chiusura.
          </p>
          <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 480 }}>
            <div>
              <label className="field-label" htmlFor="close-date">Data di fine effettiva *</label>
              <input
                id="close-date"
                type="date"
                className="field-input"
                value={closeDate}
                min={dataApertura || undefined}
                max={oggi}
                onChange={(e) => setCloseDate(e.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="feedback-performance">Feedback del cliente sulla performance della risorsa *</label>
              <textarea
                id="feedback-performance"
                className="field-textarea"
                value={feedbackPerformance}
                onChange={(e) => setFeedbackPerformance(e.target.value)}
                placeholder="Valutazione del cliente sull'operato della risorsa…"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="feedback-qualita">Feedback sulla qualità del progetto *</label>
              <textarea
                id="feedback-qualita"
                className="field-textarea"
                value={feedbackQualita}
                onChange={(e) => setFeedbackQualita(e.target.value)}
                placeholder="Valutazione della qualità complessiva del progetto…"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="note-chiusura">Note di chiusura</label>
              <textarea
                id="note-chiusura"
                className="field-textarea"
                value={noteChiusura}
                onChange={(e) => setNoteChiusura(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-accent"
              disabled={closing || !closeDate || closeDate > oggi || !feedbackPerformance.trim() || !feedbackQualita.trim()}
              style={{ justifySelf: 'start' }}
              onClick={handleClose}
            >
              {closing ? 'Chiusura in corso…' : 'Chiudi esperienza'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
